import React from "react";
import { motion } from "framer-motion";

const LOGO_SRC = "/ahjin-logo-v3.webp";

/**
 * AhJinLogo — uses the clean square brand asset directly.
 */
export const AhJinLogo = ({ size = 200, animate = true, showWordmark = true, showMark = true, align = "center" }) => {
  return (
    <div className={`flex flex-col select-none ${align === "left" ? "items-center lg:items-start" : "items-center"}`} data-testid="ahjin-logo">
      {showMark && (
      <motion.div
        initial={animate ? { opacity: 0, scale: 0.85, filter: "blur(20px)" } : false}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
        style={{ width: size, height: size }}
      >
        {/* Glow halo behind logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.25, 0.55, 0.25] }}
          transition={{ delay: 1.2, duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 -m-12 rounded-full bg-purple-600/30 blur-3xl"
        />

        {/* Champagne–purple conic gradient ring (matches header mark) */}
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 0, rotate: -40 }}
          animate={{ opacity: 1, rotate: 0 }}
          transition={{ delay: animate ? 0.3 : 0, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            padding: Math.max(2, Math.round(size * 0.018)),
            background:
              "conic-gradient(from 140deg, rgba(229,197,111,0.7), rgba(168,85,247,0.6), rgba(229,197,111,0.7), rgba(124,58,237,0.55), rgba(229,197,111,0.7))",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            filter: "drop-shadow(0 0 18px rgba(229,197,111,0.35)) drop-shadow(0 0 36px rgba(168,85,247,0.30))",
          }}
        />

        {/* Logo image — enlarged so the nebula disc fills the ring; gold arcs sweep past it */}
        <div className="relative w-full h-full breath" style={{ zIndex: 1 }}>
          <img
            src={LOGO_SRC}
            alt="Ah-Jin mark"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transform: "scale(1.32)",
              filter: "brightness(1.2) saturate(1.15)",
            }}
            data-testid="ahjin-logo-img"
          />
        </div>
      </motion.div>
      )}

      {showWordmark && (
        <motion.div
          initial={{ opacity: 0, y: 14, letterSpacing: "0.4em" }}
          animate={{ opacity: 1, y: 0, letterSpacing: "0.55em" }}
          transition={{ delay: animate ? 0.6 : 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-zinc-100/95 text-2xl sm:text-3xl mt-4"
          style={{ letterSpacing: "0.55em", paddingLeft: "0.55em" }}
          data-testid="ahjin-wordmark"
        >
          AH–JIN
        </motion.div>
      )}
      {showWordmark && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: animate ? 0.9 : 0.25, duration: 0.7 }}
          className="font-display italic text-purple-200/90 text-lg sm:text-xl mt-2 tracking-wide text-glow"
          data-testid="ahjin-tagline"
        >
          I shall progress forward
        </motion.div>
      )}
      {showWordmark && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: animate ? 1.2 : 0.4, duration: 0.6 }}
          className="text-[10px] mt-3 tracking-[0.45em] text-zinc-500 uppercase"
        >
          Co. — Strategic Capital & Technology
        </motion.div>
      )}
    </div>
  );
};

/* Compact mark for header */
export const AhJinMark = ({ className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <div
      className="relative w-9 h-9 rounded-full overflow-hidden flex items-center justify-center"
      style={{
        background:
          "radial-gradient(circle at 35% 35%, rgba(229,197,111,0.25), transparent 60%)",
      }}
    >
      <span
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          padding: "1px",
          background:
            "conic-gradient(from 140deg, rgba(229,197,111,0.6), rgba(168,85,247,0.5), rgba(229,197,111,0.6))",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      <img
        src={LOGO_SRC}
        alt="Ah-Jin"
        className="w-8 h-8 object-contain rounded-full"
        style={{ filter: "brightness(1.25) saturate(1.15)", transform: "scale(1.32)" }}
        draggable={false}
        data-testid="ahjin-mark-img"
      />
    </div>
    <span className="font-display tracking-[0.42em] text-sm text-zinc-100">AH–JIN</span>
  </div>
);

export default AhJinLogo;
